/**
 * The ladder an opening climbs, in one place.
 *
 * Every list, the tracker under a job, the API's validation and the Python
 * side used to each carry their own copy of "what comes after applied", and
 * they drifted: the tracker offered "they replied" as a step that led nowhere,
 * while the row buttons offered something else. Pure data, no React and no
 * database, so web/tests can hold it to account.
 *
 * `replied` is gone. "They replied" said nothing about what they said; the
 * step that matters is an interview. No row ever rested on it in production
 * when the word was retired (#30, #40), so nothing was migrated. A stray one
 * from some other copy of the log still has to render, which is why
 * `isApplication` and the rest take a string and answer for it rather than
 * refusing a value they do not know.
 */
export const STATUSES = [
  "new",
  "maybe",
  "passed",
  "preparing",
  "applied",
  "interview_requested",
  "interviewing",
  "offer",
  "rejected",
] as const;

export type Status = (typeof STATUSES)[number];

export function isStatus(x: unknown): x is Status {
  return typeof x === "string" && (STATUSES as readonly string[]).includes(x);
}

/**
 * A standing they reached. `new` is not one of these.
 *
 * `new` is never written: it is `coalesce(cur.status, 'new')`'s fallback, the
 * word this code uses among itself for "there is no decision row". The
 * interface has no word for it — A16 took the chip off the company card, and
 * the ruling is the whole product's: a label saying "nothing has happened
 * here" makes the rows where something *did* harder to find.
 *
 * So `status.new` is not in the i18n tables, and this type is how that stays
 * true. Anything that renders a status is keyed on `Stood`, and a caller
 * holding a plain `Status` has to say what it does with `new` before it can
 * print anything — `tsc` asks, rather than a reader noticing 「新到」 in a
 * screenshot six weeks later.
 */
export type Stood = Exclude<Status, "new">;

export function stood(s: Status): s is Stood {
  return s !== "new";
}

/**
 * What can happen next, given where an opening already is. The order is the
 * order the buttons appear in: the step forward first, the way out last.
 *
 * `preparing` is reached from the apply button, not offered as an answer, so
 * it is absent from `new` and `maybe` on purpose — see CardActions.
 */
export const NEXT: Record<Status, readonly Status[]> = {
  new: ["maybe", "passed"],
  maybe: ["passed"],
  preparing: ["applied", "passed"],
  applied: ["interview_requested", "rejected"],
  interview_requested: ["interviewing", "rejected"],
  interviewing: ["offer", "rejected"],
  offer: [],
  rejected: [],
  passed: [],
};

export function isTerminal(s: Status): boolean {
  return NEXT[s].length === 0;
}

/** The states that mean they sent it — applied and everything after, the
 *  outcomes included. Where the tracker lives, what the apply list shows,
 *  and what a later pass must not be read as a rejection of. */
const APPLICATION: readonly Status[] = [
  "applied",
  "interview_requested",
  "interviewing",
  "offer",
  "rejected",
];

export function isApplication(s: Status | undefined | null): boolean {
  return !!s && APPLICATION.includes(s);
}

/** Still in play after being sent: applied and the two interview states.
 *  Not `offer` — that one is settled — and not `rejected`. */
export function isInFlight(s: Status | undefined | null): boolean {
  return s === "applied" || s === "interview_requested" || s === "interviewing";
}

/** An interview asked for or under way — the news the overview counts. */
export function isInterview(s: Status | undefined | null): boolean {
  return s === "interview_requested" || s === "interviewing";
}

/** An interview was asked for, or has happened: interview_requested and every
 *  step past it that came out of one. `rejected` is not here — a rejection
 *  says nothing about whether anyone talked to them. What the deep research on
 *  a company page is gated on: it is interview preparation, and shows once they
 *  have an interview there (#47). */
export function isInterviewStage(s: Status | undefined | null): boolean {
  return s === "interview_requested" || s === "interviewing" || s === "offer";
}

/** A decision row as far as the trail needs it (domain/openings.ts DecisionRow). */
export type Step<S extends string = Status> = { id: number; status: S; comment: string | null; at: Date };

/**
 * The status changes an opening went through, oldest first, with what was
 * taken back taken out (#241).
 *
 * Decisions are appended and never deleted (platform/commands.ts `undo`), so
 * the log is not the trail. An undo of decision X writes a row carrying the
 * status from before X and the comment `undo:X`; read raw, the log said
 * 「收到面试邀请」 twice — once for the invitation, once for the marker that
 * took 「开始面试了」 back — and the undone 「开始面试了」 was still in it.
 *
 * So a marker is read as what `undo` does: the opening goes back to where it
 * stood before X, and the trail with it — everything from X on is gone. That
 * holds when X is itself a marker (pressing 撤销 twice puts back what the
 * first press removed, and so does this) and when X is not the newest row.
 * A marker naming a row this log does not have is ignored; the marker itself
 * is never a step. A decision in the status it is already in (a note saved
 * on the card is one) is not a change either.
 *
 * Pure and in the domain because the card (jobs/adapt.ts) and the interview
 * page's reading view both drew it, and each drew its own reading — the view
 * is the card now (Issue 253), and the rule stays where both could reach it.
 */
export function trail<S extends string, T extends Step<S>>(decisions: readonly T[]): T[] {
  return standing<T>(decisions).filter((d, i, out) => i === 0 || out[i - 1].status !== d.status);
}

/**
 * Every decision that stands, oldest first: the log with each undo marker
 * read as `trail` reads it (what it took back is gone, and the marker is not
 * a step), but keeping a decision in the status it was already in — a note
 * saved on the card is one — which `trail` folds away because it is not a
 * change. What they wrote down is still theirs to see ("export my data",
 * #1187); `trail` is this, with the repeats taken out, so the two cannot
 * read an undo differently.
 */
export function standing<T extends { id: number; comment: string | null }>(decisions: readonly T[]): T[] {
  const log = [...decisions].sort((a, b) => a.id - b.id);
  // What stood just before each row was read.
  const before = new Map<number, T[]>();
  let out: T[] = [];
  for (const d of log) {
    before.set(d.id, out);
    const undone = /^undo:(\d+)$/.exec(d.comment ?? "");
    if (undone) {
      const back = before.get(Number(undone[1]));
      if (back) out = back;
      continue;
    }
    out = [...out, d];
  }
  return out;
}

export type Level = "high" | "mid" | "low";

/**
 * How far an application got, as a number that orders the outcomes.
 *
 * Not a level and not stored: match_level keeps three values, so their grade
 * and the ruler's stay comparable cell for cell. This is the read point for the
 * learning that comes later (roadmap D2) — an offer is worth more than an
 * interview, an interview more than a sent application — and for anything
 * that wants to weight companies by how far they got with them.
 */
export const OUTCOME_RANK: Record<Status, number> = {
  new: 0,
  maybe: 0,
  passed: 0,
  preparing: 0,
  rejected: 0,
  applied: 1,
  interview_requested: 2,
  interviewing: 3,
  offer: 4,
};
